import React from 'react'
import { Stack, Typography, useTheme } from '@mui/material'
import { ChargingStatus } from 'src/Routes/EMobility/EMobilityModels'
import { E3Hourglass, J1ToprightarrowC, J1DownleftarrowC, G1Nowifi } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DynamicPriceIconOnWallboxCards } from 'src/Libs/dynamic-power-prices/components/power-price-settings/DynamicPriceIconOnWallboxCards'

interface Props {
  title?: string
  isOffline?: boolean
  isChargingSessionPaused?: boolean
  chargingStatus?: ChargingStatus
  energyAll?: number
  showDynamicPricesIcon: boolean
  isCharging: boolean
  isPriceBelowLimit: boolean
  isDynamicPowerPriceEnabledForWallboxes: boolean
}

/**
 * Header for wallbox widgets. displays a title and current status of wallbox.
 */
const WallboxWidgetHeader: React.FC<Props> = ({
  title,
  isOffline,
  isChargingSessionPaused,
  chargingStatus: wallboxChargingStatus,
  energyAll,
  showDynamicPricesIcon,
  isCharging,
  isPriceBelowLimit,
  isDynamicPowerPriceEnabledForWallboxes,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()

  const getSubtitle = (): string | React.ReactNode | undefined => {
    if (isOffline) {
      return (
        <>
          {t('dashboard.liveView.widgets.offline')}
          <G1Nowifi sx={{ fontSize: '1em', marginBlock: 0, marginLeft: 1, color: theme.palette.error.main }} />
        </>
      )
    }

    if (wallboxChargingStatus === 'CHARGING') {
      return (
        <>
          {t('dashboard.liveView.widgets.loading')} <J1ToprightarrowC sx={{ fontSize: '1em', marginBlock: 'auto' }} />
        </>
      )
    }

    if (wallboxChargingStatus === 'DISCHARGING') {
      return (
        <>
          {t('dashboard.liveView.widgets.disloading')} <J1DownleftarrowC sx={{ fontSize: '1em', marginBlock: 'auto' }} />
        </>
      )
    }

    if (wallboxChargingStatus === 'WAIT' || isChargingSessionPaused) {
      return (
        <>
          {energyAll === 0 ? t('dashboard.liveView.widgets.waitToCharge') : t('dashboard.liveView.widgets.paused')}
          <E3Hourglass sx={{ fontSize: '1em', marginBlock: 'auto' }} />
        </>
      )
    }

    if (wallboxChargingStatus === 'UNKNOWN') return t('dashboard.liveView.widgets.unknown')

    if (wallboxChargingStatus === 'WAIT_PV') return t('dashboard.liveView.widgets.waiting-for-pv')

    if (wallboxChargingStatus === 'STANDBY') return t('dashboard.liveView.widgets.standby')

    return t('dashboard.liveView.widgets.available')
  }
  return (
    <Stack justifyContent="center" sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="h3"
        noWrap
        sx={{
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title ?? ''}
      </Typography>
      <Typography variant="caption" display="flex" alignItems="center">
        <>
          {getSubtitle()}
          {showDynamicPricesIcon ? (
            <DynamicPriceIconOnWallboxCards
              isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabledForWallboxes}
              isCharging={isCharging}
              isPriceBelowLimit={isPriceBelowLimit}
            />
          ) : null}
        </>
      </Typography>
    </Stack>
  )
}

export default WallboxWidgetHeader
